/**
 * selectors/watchlistSummaries.ts
 *
 * Memoized watchlist row summary selector for MER Algo Footprints v4.3+
 *
 * WatchlistSidebar previously re-ran gamma → zones → smc → regime for every
 * symbol on every render, multiplied by watchlist size. This replaces those
 * inline calls with a cached lightweight summary.
 *
 * Usage (in WatchlistSidebar WLRow):
 *   const summary = getWatchlistRowSummary(symbol, data, settings, weights);
 */

import { now } from '../../../utils/clock';
import {
  buildWatchlistRowSummary, WatchlistRowSummary,
} from '../../../services/buildSymbolContext';
import { AppSettings, ScoringWeights, DataState } from '../../../types';

// ─── Cache ────────────────────────────────────────────────────────────────────

interface RowCacheEntry {
  summary:      WatchlistRowSummary;
  lastCandleTs: number;
  candleCount:  number;
  lastClose:    number;
  computedAt:   number;
}

// ─── Pure compute (no cache, no time dependency) ──────────────────────────────

export function computeWatchlistRowSummary(
  symbol:   string,
  data:     DataState | undefined,
  settings: AppSettings,
  weights:  ScoringWeights
): WatchlistRowSummary {
  const empty: WatchlistRowSummary = {
    symbol,
    curPrice:    null,
    chgPct:      null,
    regimeLabel: '—',
    regimeColor: 'var(--blue-dim)',
    smcBias:     'neutral',
    rvol:        null,
    dataQuality: {
      hasCandles: false, hasSufficientBars: false,
      hasOptions: false, hasGreeks: false,
      hasRealData: false, completeness: 0, warnings: [],
    },
  };
  if (!data?.candles?.length) return empty;
  return buildWatchlistRowSummary(symbol, data, settings, weights);
}

const _rowCache  = new Map<string, RowCacheEntry>();
const MAX_ROW_AGE = 15_000; // 15 s

// ─── Single-symbol selector ───────────────────────────────────────────────────

export function getWatchlistRowSummary(
  symbol:   string,
  data:     DataState | undefined,
  settings: AppSettings,
  weights:  ScoringWeights
): WatchlistRowSummary {
  const empty: WatchlistRowSummary = {
    symbol,
    curPrice:    null,
    chgPct:      null,
    regimeLabel: '—',
    regimeColor: 'var(--blue-dim)',
    smcBias:     'neutral',
    rvol:        null,
    dataQuality: {
      hasCandles: false, hasSufficientBars: false,
      hasOptions: false, hasGreeks: false,
      hasRealData: false, completeness: 0, warnings: [],
    },
  };

  if (!data?.candles?.length) return empty;

  const candles      = data.candles;
  const lastCandleTs = candles[candles.length - 1].ts;
  const candleCount  = candles.length;
  const cached       = _rowCache.get(symbol);

  if (
    cached &&
    cached.lastCandleTs === lastCandleTs &&
    cached.candleCount  === candleCount  &&
    (now() - cached.computedAt) < MAX_ROW_AGE
  ) {
    return cached.summary;
  }

  const summary = buildWatchlistRowSummary(symbol, data, settings, weights);
  _rowCache.set(symbol, { summary, lastCandleTs, candleCount, lastClose: candles[candles.length-1].close, computedAt: now() });
  return summary;
}

// ─── Batch selector ───────────────────────────────────────────────────────────

export function getAllWatchlistSummaries(
  symbols:    string[],
  symbolData: Record<string, DataState | undefined>,
  settings:   AppSettings,
  weights:    ScoringWeights
): Record<string, WatchlistRowSummary> {
  const result: Record<string, WatchlistRowSummary> = {};
  for (const sym of symbols) {
    result[sym] = getWatchlistRowSummary(sym, symbolData[sym], settings, weights);
  }
  return result;
}

export function invalidateRowSummary(symbol: string): void {
  _rowCache.delete(symbol);
}
